/**
 * Branding Context
 * Provides the effective app bar color based on tenant branding settings
 * Falls back to the application default (Burgundy #800020) if not set
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useTenantLocaleSettings } from '../hooks/useTenantSettings';
import appConfig from '../../shared-config/app.config.json';

// Default app brand color from config
const DEFAULT_BRAND_COLOR = appConfig.branding.primaryColor;

interface BrandingContextType {
  /** The effective brand color (tenant override or app default) */
  brandColor: string;
  /** Whether the brand color is a tenant override (true) or app default (false) */
  isCustomBrandColor: boolean;
  /** Loading state while fetching tenant settings */
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { data: localeSettings, isLoading } = useTenantLocaleSettings();

  const value = useMemo(() => {
    const tenantBrandColor = localeSettings?.brand_color;
    const brandColor = tenantBrandColor || DEFAULT_BRAND_COLOR;
    const isCustomBrandColor = !!tenantBrandColor;

    return {
      brandColor,
      isCustomBrandColor,
      isLoading,
    };
  }, [localeSettings?.brand_color, isLoading]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextType {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

/**
 * Hook to get just the brand color (convenience wrapper)
 */
export function useBrandColor(): string {
  const { brandColor } = useBranding();
  return brandColor;
}

/**
 * Get the default brand color without needing context
 * Useful for static contexts or fallbacks
 */
export function getDefaultBrandColor(): string {
  return DEFAULT_BRAND_COLOR;
}
